import { useState } from '#app'
import { useApiConfig } from '~/config/api'
import {
  loadBarcodingSettings, parseBarcodingSettings, saveBarcodingSettings, type BarcodingSettings,
} from '~/utils/barcodingSettings'
import { reach } from '~/utils/lookupReach'

/**
 * The site-wide barcoding settings (utils/barcodingSettings.ts), for a screen.
 *
 * `settings` starts as the tablet's own copy, so a screen opened out of range
 * has an answer at once. `refresh()` asks DockAutomation and, if it answers,
 * replaces both. No answer changes nothing and is not an error: the tablet
 * carries on with what it last knew.
 */
export function useBarcodingSettings() {
  const settings = useState<BarcodingSettings>('barcoding-settings', () => loadBarcodingSettings())

  async function refresh(): Promise<void> {
    try {
      const apiConfig = useApiConfig()
      const response = await reach(`${apiConfig.baseURL}/barcoding-settings`, {
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) return
      const latest = parseBarcodingSettings(await response.json())
      settings.value = latest
      saveBarcodingSettings(latest)
    } catch {
      // Out of range, or the server is busy: keep what the tablet has.
    }
  }

  return { settings, refresh }
}
